import { CustomDialog } from "@/components/ui-elements";
import { useDeleteWorkflow } from "@/features/workflow/api/deleteWorkflow";
import useWorkflowStore from "@/store/workflow/workflowStore";
import { queryClient } from "@/lib/react-query";
import type { ForwardRefRenderFunction } from "react";
import { forwardRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";

interface DeleteGroupPopupProps {
  my_workflow_id: string | undefined; // 表示している画面のワークフローのID
  selected_workflow_id: string; // 削除するワークフローのID
  selected_workflow_label: string;
}

const DeleteGroupPopupComponent: ForwardRefRenderFunction<
  HTMLDivElement,
  DeleteGroupPopupProps
> = ({ my_workflow_id, selected_workflow_id, selected_workflow_label }) => {
  const { isDeleteGroupPopupOpen, closeDeleteGroupPopup } = useWorkflowStore();
  const deleteWorkflowMutation = useDeleteWorkflow();
  const navigation = useNavigate();

  // ワークフローを削除する関数
  const handleDelete = useCallback(async () => {
    if (!selected_workflow_id) {
      return;
    }
    try {
      await deleteWorkflowMutation
        .mutateAsync(selected_workflow_id)
        .then(() => {
          closeDeleteGroupPopup();
          // ルールに関連する実行履歴キャッシュを無効化
          queryClient.invalidateQueries(["UploadSets"]);
        });
      closeDeleteGroupPopup();
      if (my_workflow_id === selected_workflow_id) {
        navigation("/views");
      }
    } catch (error) {
      closeDeleteGroupPopup();
    }
  }, [
    closeDeleteGroupPopup,
    deleteWorkflowMutation,
    my_workflow_id,
    navigation,
    selected_workflow_id,
  ]);

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // NOTE: Enterキー押下でCSV登録できるようにする
      if (e.key === "Enter") {
        handleDelete();
      }
    };

    document.addEventListener("keypress", handleKeyPress);

    // NOTE: clean up
    return () => {
      document.removeEventListener("keypress", handleKeyPress);
    };
  }, [handleDelete]);

  return (
    <CustomDialog
      open={isDeleteGroupPopupOpen}
      onClose={closeDeleteGroupPopup}
      title={`Scan設定「${selected_workflow_label}」を削除`}
      description={`Scan設定「${selected_workflow_label}」の削除を実行します。削除したジョブは元に戻すことができませんがよろしいですか？`}
      primaryButtonText="実行する"
      primaryButtonAction={handleDelete}
      secondaryButtonText="キャンセル"
      secondaryButtonAction={closeDeleteGroupPopup}
    ></CustomDialog>
  );
};

const DeleteGroupPopup = forwardRef(DeleteGroupPopupComponent);
DeleteGroupPopup.displayName = "DeleteGroupPopup";

export default DeleteGroupPopup;
